// Element UI 的实际列配置包含插槽列和多级表头；在此统一处理，避免各页面重复设宽。
const FULL_VALUE_CLASS = "lcsj-table-full-value";
const DATE_TIME = /\d{4}[-/]\d{1,2}[-/]\d{1,2}[ T]\d{1,2}:\d{2}(?::\d{2})?/;
const DATE_ONLY = /^\s*\d{4}(?:[-/]\d{1,2}[-/]\d{1,2}|年\d{1,2}月\d{1,2}日)\s*$/;
const TIME_ONLY = /^\s*\d{1,2}:\d{2}(?::\d{2})?\s*$/;
const COLUMN_WIDTHS = { identifier: 240, datetime: 200, date: 120, time: 110, store: 120, processing: 120 };

function isStoreNameColumn(column) {
	const label = column.label || "";
	const prop = column.property || column.prop || "";
	return /storeName$/i.test(prop) || /^(?:(?:所属|需求|收货|调入|调出|申购|开卡|办卡|充值|消费|退款|转入|转出)?门店(?:名称)?|店名)$/.test(label);
}

function fullValueKind(column, values = []) {
	if (column.type && column.type !== "default") return "";
	const label = column.label || "";
	const prop = column.property || column.prop || "";
	if (/序号|数量|次数|时长|耗时/.test(label)) return "";
	if (/编号|编码|单号|流水号|卡号|档案号|会员号|批次号/.test(label) || /(?:Code|No|Number|Serial)$/.test(prop) || /^(code|serial|number)$/i.test(prop)) return "identifier";
	if (isStoreNameColumn(column)) return "store";
	if (label === "加工类型" || /(?:machinTypeStr|processTypeStr)$/.test(prop)) return "processing";
	// Rendered values take precedence: labels such as “取镜时间” may actually show only a date.
	if (values.some(value => DATE_TIME.test(value))) return "datetime";
	if (values.some(value => DATE_ONLY.test(value))) return "date";
	if (values.some(value => TIME_ONLY.test(value))) return "time";
	if (/时间/.test(label) || /(?:Time|At)$/.test(prop)) return "datetime";
	if (/日期/.test(label) || /Date$/.test(prop)) return "date";
	return "";
}

export function fullValueMinWidth(column, values = []) {
	return COLUMN_WIDTHS[fullValueKind(column, values)] || 0;
}

// 每次只保留当前表格内容的测量缓存，翻页不会无限积累。
// 所有 DOM 写入、尺寸读取、清理各自成批进行，避免每个单元格强制一次布局。
export function fitFullValueColumns(table, cache = new Map(), storeOnly = false) {
	if (!table.$el.clientWidth) return;
	const columns = table.store.states.columns || [];
	const plans = [];
	const measurements = [];
	const nextCache = new Map();
	columns.forEach(column => {
		if (storeOnly && !isStoreNameColumn(column)) return;
		const cells = Array.from(table.$el.querySelectorAll(`.${column.id} > .cell`))
			.filter(cell => cell.closest(".el-table") === table.$el && !cell.closest(".el-table__fixed, .el-table__fixed-right"));
		const kind = fullValueKind(column, cells.map(cell => cell.textContent));
		const minimum = COLUMN_WIDTHS[kind];
		if (!minimum) return;
		const previous = cache.get(column.id) || new Map();
		const widths = new Map();
		nextCache.set(column.id, widths);
		cells.forEach(cell => {
			const style = window.getComputedStyle(cell);
			const key = JSON.stringify([cell.innerHTML, cell.className, cell.style.cssText,
				cell.parentNode.tagName, style.font, style.letterSpacing, style.paddingLeft, style.paddingRight]);
			if (widths.has(key)) return;
			if (previous.has(key)) {
				widths.set(key, previous.get(key));
				return;
			}
			widths.set(key, 0);
			const clone = cell.cloneNode(true);
			clone.classList.add(FULL_VALUE_CLASS);
			Object.assign(clone.style, {
				position: "absolute", visibility: "hidden", pointerEvents: "none",
				width: "max-content", minWidth: "0", maxWidth: "none", height: "auto"
			});
			clone.setAttribute("aria-hidden", "true");
			measurements.push({ parent: cell.parentNode, clone, widths, key });
		});
		plans.push({ column, minimum, widths, temporal: ["datetime", "date", "time"].includes(kind) });
	});
	try {
		measurements.forEach(item => item.parent.appendChild(item.clone));
		measurements.forEach(item => {
			item.widths.set(item.key, Math.ceil(Math.max(item.clone.getBoundingClientRect().width, item.clone.scrollWidth)) + 4);
		});
	} finally {
		measurements.forEach(item => item.clone.remove());
	}
	cache.clear();
	nextCache.forEach((value, key) => cache.set(key, value));
	let changed = false;
	plans.forEach(({ column, minimum, widths, temporal }) => {
		const classes = (column.className || "").split(/\s+/).filter(Boolean);
		if (!classes.includes(FULL_VALUE_CLASS)) {
			column.className = [...classes, FULL_VALUE_CLASS].join(" ");
			changed = true;
		}
		if (column.showOverflowTooltip) {
			column.showOverflowTooltip = false;
			changed = true;
		}
		// Dates can shrink after pagination or async loading; do not retain the previous 200px minimum.
		let width = temporal ? minimum : Math.max(minimum, Number(column.minWidth) || 0, Number(column.width) || 0);
		widths.forEach(value => { width = Math.max(width, value); });
		if (column.minWidth !== width || (temporal ? column.width !== width : column.width && column.width < width)) {
			column.minWidth = width;
			if (temporal || column.width) column.width = width;
			changed = true;
		}
	});
	if (changed) table.doLayout();
}

export default function installTableFullValue(Table) {
	Table.mixins = (Table.mixins || []).concat({
		mounted() {
			// 特殊报表只统一门店列，其他字段保留原排版。
			const storeOnly = !this.$el.classList.contains("lcsj-data-table");
			let frame = null;
			let stopped = false;
			const measurementCache = new Map();
			const observe = () => observer.observe(this.$el, { childList: true, subtree: true, characterData: true });
			const schedule = () => {
				if (stopped || frame !== null) return;
				frame = requestAnimationFrame(() => {
					frame = null;
					observer.disconnect();
					try { fitFullValueColumns(this, measurementCache, storeOnly); } finally { if (!stopped) observe(); }
				});
			};
			const observer = new MutationObserver(schedule);
			const resizeObserver = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(schedule);
			observe();
			if (resizeObserver) resizeObserver.observe(this.$el);
			else window.addEventListener("resize", schedule);
			this.$on("header-dragend", schedule);
			this._scheduleFullValueColumns = schedule;
			this._stopFullValueColumns = () => {
				stopped = true;
				measurementCache.clear();
				if (frame !== null) cancelAnimationFrame(frame);
				observer.disconnect();
				if (resizeObserver) resizeObserver.disconnect();
				else window.removeEventListener("resize", schedule);
				this.$off("header-dragend", schedule);
			};
			schedule();
		},
		updated() {
			if (this._scheduleFullValueColumns) this._scheduleFullValueColumns();
		},
		beforeDestroy() {
			if (this._stopFullValueColumns) this._stopFullValueColumns();
		}
	});
}
